/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
"use client";

import React, { useCallback, useEffect, useState } from "react";
import HomePageSearchRequest from "@/models/HomePageSearchRequest";
import { HomePageSearchResponse } from "@/models/HomePageSearchResponse";
import DEFAULT_SEARCH_REQUEST from "@/constants/DefaultSearchRequest";
import FiltersSection from "./sections/FiltersSection";
import ResultsSection from "./sections/ResultsSection";
import PaginationSection from "./sections/PaginationSection";
import { BuildUrl } from "@/actions/search/services/AdSearchUrlService";
import { useBodyBackdrop } from "@/components/search/useBodyBackdrop";
import { usePathname } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import { Box } from "@mui/material";
import { AdManagerProvider } from "@/app/layout/useAdManager";
import { SEARCH_ADS } from "./ads";
import AdModule from "@/components/shared/AdModule";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";

type ResultsLoadingStatus = "pending" | "loading" | "loaded";

function ForSaleBody({
  searchPath,
  searchRequest,
  setSearchRequest,
  searchResponse,
  isFilterBarExpanded,
  setIsFilterBarExpanded,
  loadingStatus,
  featuredResponse,
}: {
  searchPath: string;
  searchRequest: HomePageSearchRequest;
  setSearchRequest: (
    searchRequest: HomePageSearchRequest,
    skipRefresh?: boolean,
  ) => void;
  searchResponse: HomePageSearchResponse;
  isFilterBarExpanded: boolean;
  setIsFilterBarExpanded: (isExpanded: boolean) => void;
  loadingStatus: ResultsLoadingStatus;
  featuredResponse: HomePageSearchResponse;
}) {
  const handleSearchRequestChange = (updates: any, skipRefresh?: boolean) => {
    const isResetting = updates.vertical;
    const newSearchRequest = {
      ...(isResetting ? DEFAULT_SEARCH_REQUEST : searchRequest),
      ...updates,
      page: updates.page || 1,
    };
    setSearchRequest(newSearchRequest, skipRefresh);

    if (
      typeof updates.page !== "undefined" ||
      typeof updates.sortBy !== "undefined"
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      {searchResponse && (
        <>
          <FiltersSection
            searchResponse={searchResponse}
            searchRequest={searchRequest}
            onSearchRequestChange={handleSearchRequestChange}
            isExpanded={isFilterBarExpanded}
            setIsExpanded={setIsFilterBarExpanded}
            isLoading={loadingStatus === "loading"}
          />
          {/* AJB: probably these props should be called "on" rather than "handle" too */}
          <ResultsSection
            searchPath={searchPath}
            searchResponse={searchResponse}
            searchRequest={searchRequest}
            handleSearchRequestChange={handleSearchRequestChange}
            featuredResponse={featuredResponse}
          />
          <Box
            sx={(theme) => ({
              padding: theme.spacing(8, 0),
            })}
          >
            <PaginationSection
              searchRequest={searchRequest}
              searchResponse={searchResponse}
              handleSearchRequestChange={handleSearchRequestChange}
            />
          </Box>
          <AdModule config={SEARCH_ADS.Horsezone_Search_Bottom} />
        </>
      )}
    </>
  );
}

let isFilterBarExpandedGlobal = false;

function ForSalePageContainer({
  searchPath,
  initialSearchRequest,
  initialSearchResponse,
  featuredResponse,
}: {
  searchPath: string;
  initialSearchRequest: HomePageSearchRequest;
  initialSearchResponse: HomePageSearchResponse;
  featuredResponse: HomePageSearchResponse;
}) {
  const [searchRequest, setSearchRequest] =
    useState<HomePageSearchRequest>(initialSearchRequest);
  const [loadingStatus, setLoadingStatus] =
    useState<ResultsLoadingStatus>("loaded");
  const [isFilterBarExpanded, setIsFilterBarExpanded] = useState(
    isFilterBarExpandedGlobal,
  );
  const router = useRouter();
  const pathname = usePathname();
  const { backdropOpen, setBackdropOpen } = useBodyBackdrop();

  const loadNewResults = useCallback(
    (newUrl: string) => {
      setLoadingStatus("loading");
      router.push(newUrl, undefined); // AJB: handle scroll here?
    },
    [router],
  );

  useEffect(() => {
    const newUrl = BuildUrl(searchRequest);
    const urlHasChanged = newUrl !== pathname;
    const isLoadingOneChanges = loadingStatus === "loaded" && urlHasChanged;
    const isLoadingMultipleChanges =
      loadingStatus === "pending" && !backdropOpen;
    const isFinishedLoading = loadingStatus === "loading" && !urlHasChanged;
    const isLoadingChanges = isLoadingOneChanges || isLoadingMultipleChanges;

    if (isLoadingChanges) {
      loadNewResults(newUrl);
    } else if (isFinishedLoading) {
      setLoadingStatus("loaded");
      if (!isFilterBarExpandedGlobal) {
        setBackdropOpen(false);
      }
    }
  }, [
    backdropOpen,
    loadingStatus,
    searchRequest,
    pathname,
    loadNewResults,
    setBackdropOpen,
  ]);

  const handleSetIsFilterBarExpanded = useCallback(
    (isExpanded: boolean) => {
      setIsFilterBarExpanded(isExpanded);
      isFilterBarExpandedGlobal = isExpanded;
      setBackdropOpen(isExpanded);
    },
    [setBackdropOpen],
  );

  useEffect(() => {
    if (!backdropOpen) {
      handleSetIsFilterBarExpanded(false);
    }
  }, [backdropOpen, handleSetIsFilterBarExpanded]);

  return (
    <ForSaleBody
      searchPath={searchPath}
      searchRequest={searchRequest}
      setSearchRequest={(newSearchRequest, skipRefresh) => {
        if (skipRefresh) {
          setLoadingStatus("pending");
        }
        setSearchRequest(newSearchRequest);
      }}
      searchResponse={initialSearchResponse}
      isFilterBarExpanded={isFilterBarExpanded}
      setIsFilterBarExpanded={handleSetIsFilterBarExpanded}
      loadingStatus={loadingStatus}
      featuredResponse={featuredResponse}
    />
  );
}

export default function ForSalePage({
  searchPath,
  initialSearchRequest,
  initialSearchResponse,
  featuredResponse,
}: {
  searchPath: string;
  initialSearchRequest: HomePageSearchRequest;
  initialSearchResponse: HomePageSearchResponse;
  featuredResponse: HomePageSearchResponse;
}) {
  useEffect(() => {
    CreateAnalyticEvent({
      event: {
        adIds: initialSearchResponse.results.ads.map((ad) => ad.id),
        eventType: "AD_IMPRESSIONS#SEARCH#ALL_RESULTS",
      },
    });
  }, [initialSearchResponse]);

  return (
    <AdManagerProvider ads={SEARCH_ADS}>
      <ForSalePageContainer
        searchPath={searchPath}
        initialSearchRequest={initialSearchRequest}
        initialSearchResponse={initialSearchResponse}
        featuredResponse={featuredResponse}
      />
    </AdManagerProvider>
  );
}
